/**
 * AI4BA — catalog skill/workflow ba-* gắn theo quy trình BA (DOC-BA-PROCESS-OVERVIEW).
 * Chỉ chứa prefix ba-*; không gồm bmad-* / stitch::*.
 */

export type BaSkillRef = {
  id: string;
  purpose: string;
  whenToUse?: string;
  input?: string;
};

export type BaFlowStep = {
  id: string;
  title: string;
  summary?: string;
  skills: BaSkillRef[];
  /** Id slide phase trong carousel để jump từ sơ đồ master */
  phaseId?: string;
  /** Label ngắn trên node sơ đồ */
  shortLabel?: string;
};

export type BaProcessFlow = {
  id: string;
  title: string;
  subtitle?: string;
  /** Legacy PNG — giữ để rollback; UI master dùng BaSkillPathDiagram */
  diagramImage?: string;
  steps: BaFlowStep[];
};

const ELICITATION_SKILLS: BaSkillRef[] = [
  {
    id: 'ba-competitor-analyze',
    purpose: 'Phân tích đối thủ, bóc tách hệ sinh thái, đánh giá ưu/nhược và đề xuất USP.',
    whenToUse: 'Khi cần nghiên cứu sản phẩm đối thủ để học hỏi hoặc tìm điểm khác biệt.',
    input: 'Tên đối thủ, danh sách điểm chạm (Web/App), tài liệu liên quan.',
  },
  {
    id: 'ba-concept-modeling',
    purpose: 'Mô hình hóa khái niệm nghiệp vụ (Noun/Verb), sinh sơ đồ và mock data.',
    whenToUse: 'Khi cần bóc tách bài toán phức tạp thành khái niệm dễ hiểu cho team.',
    input: 'Tài liệu mô tả, email, ghi chú, luật nghiệp vụ.',
  },
  {
    id: 'ba-domain-analysis',
    purpose: 'Tìm hiểu domain chuyên sâu theo quy trình 8 bước, xuất Markdown/Word.',
    whenToUse: 'Khi cần phân tích domain mới hoặc lập kế hoạch dự án.',
    input: 'Tên domain/subdomain, tên file output mong muốn.',
  },
  {
    id: 'ba-elicitation-qna-gen',
    purpose: 'Tạo bộ câu hỏi khơi gợi yêu cầu dựa trên khoảng trống thông tin.',
    whenToUse: 'Chuẩn bị phỏng vấn hoặc làm rõ vùng chưa rõ.',
    input: 'File research domain, kết quả khơi gợi trước, context dự án.',
  },
  {
    id: 'ba-elicitation-result-update',
    purpose: 'Cập nhật kết quả khơi gợi vào tracking, history, summary.',
    whenToUse: 'Sau họp stakeholder hoặc nhận tài liệu mới.',
    input: 'Meeting notes, tài liệu stakeholder, file listQA.',
  },
  {
    id: 'ba-elicitation-validate',
    purpose: 'Đối chiếu mâu thuẫn / khoảng trống giữa các nguồn elicitation.',
    whenToUse: 'Khi có nhiều stakeholder và cần kiểm tra nhất quán.',
    input: 'Danh sách file kết quả khơi gợi (history).',
  },
  {
    id: 'ba-form-mockup-gen',
    purpose: 'Trích xuất thực thể/chức năng từ biểu mẫu và thiết kế HTML mockup.',
    whenToUse: 'Khi KH cung cấp ảnh form/Excel cần cấu trúc hóa.',
    input: 'Mục tiêu dự án, ảnh biểu mẫu/template.',
  },
  {
    id: 'ba-interview-doc-gen',
    purpose: 'Đổi danh sách câu hỏi .md thành file phỏng vấn .docx.',
    whenToUse: 'Trước buổi phỏng vấn cần bản cứng/file chia sẻ.',
    input: 'File danh sách câu hỏi .md.',
  },
  {
    id: 'ba-saas-generalizer',
    purpose: 'Tổng quát hóa yêu cầu thô thành giải pháp B2B SaaS cấu hình động.',
    whenToUse: 'Khi muốn chuyển yêu cầu “may đo” thành framework SaaS.',
    input: 'Yêu cầu thô từ nhiều khách hàng/SME.',
  },
  {
    id: 'ba-stakeholder-doc-analysis',
    purpose: 'Đọc tài liệu stakeholder theo 10 nhóm thông tin cốt lõi.',
    whenToUse: 'Bất cứ khi nào nhận SOP/email/tài liệu từ stakeholder.',
    input: 'File hoặc nội dung stakeholder cung cấp.',
  },
  {
    id: 'ba-stakeholder-job-analyze',
    purpose: 'Phân tích việc thủ công thành chức năng hệ thống + mockup.',
    whenToUse: 'Khi tiếp nhận mô tả công việc thủ công cần chuyển thành UC.',
    input: 'Mô tả công việc, quy trình, danh sách task.',
  },
];

export const AI4BA_BA_PROCESS_FLOWS: BaProcessFlow[] = [
  {
    id: 'master',
    title: 'Bộ Skill theo quy trình',
    subtitle:
      'Flow E2E: nhánh dự án đã chạy → khơi gợi → gap → overview → PRD → UI → sequence/rule → US → CR.',
    // Legacy PNG giữ để rollback — UI master dùng BaSkillPathDiagram
    diagramImage: '/images/ai4ba/ba-master-process-e2e.png',
    steps: [
      {
        id: 'check-running',
        title: 'Kiểm tra: Dự án đã chạy?',
        shortLabel: 'Dự án đã chạy?',
        summary: 'Có hệ thống đang vận hành → tổng hợp hiện trạng + KG trước khi khơi gợi. Chưa → vào thẳng khơi gợi.',
        skills: [],
      },
      {
        id: 'asis',
        title: 'Tổng hợp hiện trạng hệ thống',
        shortLabel: 'Hiện trạng HT',
        summary: 'Chỉ khi dự án đã chạy — nắm AS-IS trước khi khơi gợi sâu.',
        phaseId: 'gap',
        skills: [
          {
            id: 'ba-process-analysis',
            purpose: 'Phân tích quy trình hiện tại (AS-IS), xuất tài liệu và vấn đề.',
            whenToUse: 'Khi cần phân tích hiện trạng đang vận hành.',
            input: 'Kết quả khơi gợi, mô tả hiện trạng.',
          },
          {
            id: 'ba-stakeholder-doc-analysis',
            purpose: 'Đọc tài liệu stakeholder → quy trình, glossary, pain point.',
            whenToUse: 'Khi nhận SOP/tài liệu hiện trạng từ KH.',
            input: 'File tài liệu stakeholder.',
          },
        ],
      },
      {
        id: 'kg',
        title: 'Tạo Knowledge Graph',
        shortLabel: 'Knowledge Graph',
        summary: 'Chuẩn hóa tài liệu hiện trạng và đồng bộ vào Knowledge Graph.',
        phaseId: 'cr',
        skills: [
          {
            id: 'ba-legacy-extractor',
            purpose:
              'Chuẩn hóa tài liệu đáp ứng ontology để dựng graph (ba-ontology-schema).',
            whenToUse: 'Khi tài liệu cũ/thô chưa đủ cấu trúc.',
            input: 'Tài liệu legacy, ba-ontology-schema.',
          },
          {
            id: 'ba-graph-sync',
            purpose: 'Merge / đồng bộ tài liệu vào Knowledge Graph.',
            whenToUse: 'Khi đã có tài liệu đúng ontology.',
            input: 'Tài liệu đã chuẩn hóa theo ba-ontology-schema.',
          },
        ],
      },
      {
        id: 'elicitation',
        title: 'Khơi gợi yêu cầu',
        shortLabel: 'Khơi gợi YC',
        summary: 'Thu thập hiện trạng + kỳ vọng tương lai (điểm hội tụ của cả hai nhánh).',
        phaseId: 'elicitation',
        skills: ELICITATION_SKILLS,
      },
      {
        id: 'gap',
        title: 'Gap Analysis',
        shortLabel: 'Gap Analysis',
        summary: 'So AS-IS với TO-BE → bảng gap + roadmap.',
        phaseId: 'gap',
        skills: [
          {
            id: 'ba-gap-analysis',
            purpose: 'So AS-IS vs TO-BE → bảng gap, phụ thuộc, roadmap.',
            whenToUse: 'Khi cần xác định khoảng trống và thứ tự triển khai.',
            input: 'Báo cáo AS-IS, mục tiêu và pain points.',
          },
          {
            id: 'ba-process-proposal',
            purpose: 'Thiết kế quy trình TO-BE (BPMN + bảng 6 cột).',
            whenToUse: 'Khi cần luồng tương lai khắc phục vấn đề hiện tại.',
            input: 'Báo cáo AS-IS, roadmap, kết quả Gap.',
          },
        ],
      },
      {
        id: 'data-model',
        title: 'Data model',
        shortLabel: 'Data model',
        summary: 'Mô hình dữ liệu / ERD / data dictionary.',
        phaseId: 'overview',
        skills: [
          {
            id: 'ba-system-overview-datamodel',
            purpose: 'Tổng quan hệ thống / data model từ schema hoặc mô tả.',
            whenToUse: 'Khi cần hiểu nhanh cấu trúc dữ liệu hệ thống.',
            input: 'Schema DB, tài liệu kỹ thuật.',
          },
          {
            id: 'ba-erd-gen',
            purpose: 'ERD, từ điển dữ liệu.',
            whenToUse: 'Khi cần cấu trúc dữ liệu tổng quan.',
            input: 'Yêu cầu nghiệp vụ, luồng quy trình.',
          },
        ],
      },
      {
        id: 'state-diagram',
        title: 'State diagram',
        shortLabel: 'State diagram',
        summary: 'Sơ đồ trạng thái thực thể / nghiệp vụ.',
        phaseId: 'overview',
        skills: [
          {
            id: 'ba-erd-gen',
            purpose: 'Sinh / bổ sung state diagram kèm mô hình dữ liệu.',
            whenToUse: 'Khi cần mô tả vòng đời trạng thái đối tượng.',
            input: 'Yêu cầu nghiệp vụ, luồng quy trình, ERD.',
          },
        ],
      },
      {
        id: 'prd',
        title: 'Tạo PRD',
        shortLabel: 'Tạo PRD',
        summary: 'Tổng hợp hồ sơ PRD và catalog quy tắc nghiệp vụ.',
        phaseId: 'architecture',
        skills: [
          {
            id: 'ba-prd-create',
            purpose: 'Workflow tổng hợp PRD (+ catalog quy tắc nghiệp vụ).',
            whenToUse: 'Khi đã có AS-IS / Gap / TO-BE / overview.',
            input: 'Kết quả elicitation, gap, TO-BE.',
          },
        ],
      },
      {
        id: 'ui-design',
        title: 'Thiết kế màn hình',
        shortLabel: 'Thiết kế MH',
        summary: 'IA, layout, UI spec và chuyển HTML → code.',
        phaseId: 'ui-design',
        skills: [
          {
            id: 'ba-ia-advisor',
            purpose: 'Brainstorm IA, sitemap, ASCII wireframe.',
            input: 'Danh sách Epic/User Story.',
          },
          {
            id: 'ba-ui-spec',
            purpose: 'Đặc tả màn hình (control, hành vi, validate).',
            input: 'Mockup/wireframe, quy trình.',
          },
          {
            id: 'ba-layout-brainstorm',
            purpose: 'Nhiều phương án layout trước khi gen UI.',
            input: 'User Story, yêu cầu nghiệp vụ.',
          },
        ],
      },
      {
        id: 'sequence',
        title: 'Sequence diagram',
        shortLabel: 'Sequence diagram',
        summary: 'Sequence diagram + đặc tả API (Request/Response/Error).',
        phaseId: 'us-spec',
        skills: [
          {
            id: 'ba-sequence-spec',
            purpose: 'Sequence diagram + đặc tả API (Request/Response/Error).',
            input: 'UI Spec, luồng màn hình.',
          },
        ],
      },
      {
        id: 'activity-rule',
        title: 'Activity diagram/Rule validate',
        shortLabel: 'Activity diagram/Rule validate',
        summary: 'Activity diagram và backend validation rule.',
        phaseId: 'us-spec',
        skills: [
          {
            id: 'ba-activity-rule-spec',
            purpose: 'Backend validation rule, flowchart logic, SQL minh họa.',
            input: 'API Spec, sequence diagram.',
          },
        ],
      },
      {
        id: 'us-spec',
        title: 'Đặc tả US theo loại',
        shortLabel: 'Đặc tả US theo loại',
        summary: 'Phân loại A–F rồi đặc tả đúng skill theo type.',
        phaseId: 'us-spec',
        skills: [
          {
            id: 'ba-user-story-by-uc-type',
            purpose: 'Entry đặc tả US — phân loại A–F, load context, route skill con.',
            input: 'UC / mô tả user / epic / danh sách UC.',
          },
        ],
      },
      {
        id: 'cr',
        title: 'Quản lý CR',
        shortLabel: 'Quản lý CR',
        summary: 'Impact analysis + Knowledge Graph MCP khi có CR.',
        phaseId: 'cr',
        skills: [
          {
            id: 'ba-impact-analysis',
            purpose: 'Phân tích tác động thay đổi yêu cầu trên tài liệu / graph.',
            whenToUse: 'Khi nhận CR / thay đổi phạm vi.',
            input: 'Mô tả CR, tài liệu liên quan.',
          },
          {
            id: 'trace_impact',
            purpose: 'MCP KG: dò node ảnh hưởng đến thành phần khác.',
          },
          {
            id: 'ba-graph-sync',
            purpose: 'Đồng bộ tài liệu sau CR vào Knowledge Graph.',
          },
        ],
      },
    ],
  },
  {
    id: 'elicitation',
    title: '1. Khơi gợi yêu cầu',
    subtitle: 'Thu thập hiện trạng và kỳ vọng tương lai của doanh nghiệp.',
    steps: [
      {
        id: 'elicitation-skills',
        title: 'AI Agent Skills — Elicitation',
        summary: 'Nhóm skill dùng khi research, phỏng vấn và cập nhật kết quả họp.',
        skills: ELICITATION_SKILLS,
      },
    ],
  },
  {
    id: 'gap',
    title: '2. Gap Analysis',
    subtitle: 'So AS-IS với mục tiêu TO-BE → bảng gap + roadmap.',
    steps: [
      {
        id: 'gap-skills',
        title: 'AI Agent Skills — Gap Analysis',
        skills: [
          {
            id: 'ba-process-analysis',
            purpose: 'Phân tích quy trình hiện tại (AS-IS).',
            whenToUse: 'Khi cần phân tích hiện trạng hệ thống/quy trình.',
            input: 'Kết quả khơi gợi, mô tả hiện trạng.',
          },
          {
            id: 'ba-gap-analysis',
            purpose: 'So AS-IS vs TO-BE → bảng gap, phụ thuộc, roadmap.',
            whenToUse: 'Khi cần xác định khoảng trống và thứ tự triển khai.',
            input: 'Báo cáo AS-IS, mục tiêu và pain points.',
          },
          {
            id: 'ba-process-proposal',
            purpose: 'Thiết kế quy trình TO-BE (BPMN + bảng 6 cột).',
            whenToUse: 'Khi cần luồng tương lai khắc phục vấn đề hiện tại.',
            input: 'Báo cáo AS-IS, roadmap, kết quả Gap.',
          },
          {
            id: 'ba-bpmn-doc-gen',
            purpose: 'Vẽ BPMN / flowchart Draw.io từ mô tả quy trình.',
            whenToUse: 'Khi cần sơ đồ quy trình trực quan chuẩn.',
            input: 'Mô tả quy trình thô (text) hoặc hình ảnh.',
          },
        ],
      },
    ],
  },
  {
    id: 'overview',
    title: '3. Phân tích tổng quan',
    subtitle: 'Định hình giải pháp mức vĩ mô trước khi đi sâu (BRD / đầu PRD).',
    steps: [
      {
        id: 'overview-skills',
        title: 'AI Agent Skills — Overview',
        skills: [
          {
            id: 'ba-product-overview-gen',
            purpose: 'Mô hình tổng quan: đối tượng, ứng dụng, tích hợp.',
            whenToUse: 'Giai đoạn đầu định hình giải pháp.',
            input: 'Kết quả khơi gợi, quy trình TO-BE.',
          },
          {
            id: 'ba-infographic-gen',
            purpose: 'Workflow gen ảnh minh họa mô hình tổng quan sản phẩm.',
            whenToUse: 'Khi cần ảnh overview trực quan từ mô tả / sơ đồ nháp.',
            input: 'Mô tả hoặc ảnh nháp mô hình tổng quan sản phẩm.',
          },
          {
            id: 'ba-usecase-list-gen',
            purpose: 'Danh sách Epic & UC theo ứng dụng, khai thác exception.',
            whenToUse: 'Khi cần bao quát khối lượng chức năng toàn hệ thống.',
            input: 'Sơ đồ tổng quan, quy trình, file khơi gợi.',
          },
          {
            id: 'ba-erd-gen',
            purpose: 'ERD, Data Dictionary, State Diagram.',
            whenToUse: 'Khi cần cấu trúc dữ liệu tổng quan.',
            input: 'Yêu cầu nghiệp vụ, luồng quy trình.',
          },
          {
            id: 'ba-dfd-level-0-gen',
            purpose: 'Sơ đồ ngữ cảnh (DFD mức 0) + bảng rà soát I/O.',
            whenToUse: 'Khi cần ranh giới hệ thống và luồng giao tiếp ngoài.',
            input: 'Tài liệu nghiệp vụ đầu vào.',
          },
        ],
      },
    ],
  },
  {
    id: 'architecture',
    title: '4. Architecture',
    subtitle: 'Chốt PRD và triển khai mô hình dữ liệu cho giai đoạn Architecture.',
    steps: [
      {
        id: 'arch-skills',
        title: 'Workflow & skill',
        skills: [
          {
            id: 'ba-prd-create',
            purpose: 'Workflow khởi tạo / tổng hợp tài liệu PRD.',
            whenToUse: 'Giai đoạn đầu hoặc khi chốt yêu cầu nghiệp vụ.',
            input: 'Yêu cầu nghiệp vụ, kết quả Elicitation / Gap / TO-BE.',
          },
          {
            id: 'ba-db-prisma-migrate',
            purpose: 'Khởi tạo schema Prisma + migration từ ERD/US.',
            whenToUse: 'Khi triển khai schema CSDL thực tế.',
            input: 'Mô hình dữ liệu (Data Entity / ERD).',
          },
        ],
      },
    ],
  },
  {
    id: 'ui-design',
    title: '5. Thiết kế màn hình',
    subtitle: 'IA → layout → UI spec → convert HTML sang code frontend.',
    steps: [
      {
        id: 'ui-design-skills',
        title: 'Design',
        skills: [
          {
            id: 'ba-ia-advisor',
            purpose: 'Brainstorm IA, sitemap và ASCII wireframe.',
            input: 'Danh sách Epic/User Story.',
          },
          {
            id: 'ba-layout-brainstorm',
            purpose: 'Đề xuất phương án layout/wireframe cho màn phức tạp.',
            input: 'User Story, yêu cầu nghiệp vụ.',
          },
          {
            id: 'ba-ui-spec',
            purpose: 'Đặc tả chi tiết màn hình, control và validate rule.',
            input: 'Hình mockup/wireframe, quy trình.',
          },
          {
            id: 'ba-user-flow-from-journey',
            purpose: 'User flow chi tiết từ hành trình khách hàng.',
            input: 'User Journey Map.',
          },
          {
            id: 'ba-ui-prompt-generator',
            purpose: 'Prompt thiết kế UI từ flow + design system.',
            input: 'User flow, design system.',
          },
        ],
      },
      {
        id: 'ui-coding-skills',
        title: 'HTML → Code',
        skills: [
          {
            id: 'ba-html-to-nextjs-code-gen',
            purpose: 'HTML → Next.js App Router (TypeScript, Tailwind).',
            input: 'File HTML prototype, repo Next.js.',
          },
          {
            id: 'ba-html-to-react-ds-code-gen',
            purpose: 'HTML → React/TS + Design System (Vite/React).',
            input: 'HTML prototype, Design System/tokens.',
          },
          {
            id: 'ba-html-to-react-native-ds-code-gen',
            purpose: 'HTML → React Native/Expo + Mobile Design System.',
            input: 'HTML prototype, repo Expo/RN.',
          },
        ],
      },
    ],
  },
  {
    id: 'us-spec',
    title: '6. Đặc tả User Story',
    subtitle: 'Entry ba-user-story-by-uc-type: phân loại A–F rồi route skill con.',
    steps: [
      {
        id: 'us-workflow',
        title: 'Workflow & bước chuẩn bị',
        skills: [
          {
            id: 'ba-user-story-by-uc-type',
            purpose: 'Entry đặc tả US — phân loại A–F, load context, route skill con.',
            input: 'UC / mô tả user / epic / danh sách UC.',
          },
          {
            id: 'ba-user-story-light',
            purpose: 'US đơn giản (L0) — không cần workflow phân loại đầy đủ.',
          },
          {
            id: 'ba-us-type-classifier',
            purpose: 'Phân loại US: A/B/C (UI) hoặc D/E/F (backend).',
          },
          {
            id: 'ba-us-context-loader',
            purpose: 'Thu thập ngữ cảnh trước khi viết spec.',
          },
        ],
      },
      {
        id: 'us-by-type',
        title: 'Đặc tả theo Type A–F',
        skills: [
          {
            id: 'ba-us-read-spec',
            purpose: 'Type A — US xem dữ liệu (+ UI Spec).',
          },
          {
            id: 'ba-us-cud-spec',
            purpose: 'Type B — US tạo/sửa/xóa (+ API Flow, Activity Rules).',
          },
          {
            id: 'ba-us-complex-spec',
            purpose: 'Type C — US nhiều màn / phức tạp (+ Sequence).',
          },
          {
            id: 'ba-us-job-spec',
            purpose: 'Type D — Job event / schedule (không UI Spec).',
          },
          {
            id: 'ba-us-api-expose-spec',
            purpose: 'Type E — API mở cho đối tác.',
          },
          {
            id: 'ba-us-webhook-spec',
            purpose: 'Type F — Webhook nhận sự kiện từ ngoài.',
          },
        ],
      },
      {
        id: 'us-support',
        title: 'Hỗ trợ Flow & Backend',
        skills: [
          {
            id: 'ba-sequence-spec',
            purpose: 'Sequence diagram + đặc tả API (Request/Response/Error).',
            input: 'UI Spec, luồng màn hình.',
          },
          {
            id: 'ba-activity-rule-spec',
            purpose: 'Backend validation rule, flowchart logic, SQL minh họa.',
            input: 'API Spec, sequence diagram.',
          },
        ],
      },
    ],
  },
  {
    id: 'cr',
    title: '7. Quản lý thay đổi (CR)',
    subtitle: 'Hai skill chuẩn bị/merge graph + MCP Knowledge Graph for BA.',
    steps: [
      {
        id: 'cr-skills',
        title: 'AI Agent Skills — Knowledge Graph',
        skills: [
          {
            id: 'ba-legacy-extractor',
            purpose:
              'Yêu cầu LLM tạo / chuẩn hóa tài liệu đáp ứng để dựng graph, kèm định nghĩa schema ba-ontology-schema.',
            whenToUse: 'Khi tài liệu cũ/thô chưa đủ cấu trúc để sync vào Knowledge Graph.',
            input: 'Tài liệu legacy, ba-ontology-schema.',
          },
          {
            id: 'ba-graph-sync',
            purpose: 'Merge / đồng bộ graph từ các tài liệu (kể cả tài liệu cũ đã chuẩn hóa) vào Knowledge Graph.',
            whenToUse: 'Khi đã có tài liệu đúng ontology cần merge vào đồ thị BA.',
            input: 'Tài liệu đã chuẩn hóa theo ba-ontology-schema.',
          },
        ],
      },
      {
        id: 'cr-kg-mcp',
        title: 'MCP Knowledge Graph for BA',
        summary:
          'Server MCP graph4ba — sync, tìm kiếm semantic và truy vết tác động trên Neo4j.',
        skills: [
          {
            id: 'sync_ba_doc',
            purpose: 'Đồng bộ file tài liệu (Markdown/CSV) vào Neo4j Graph + vector embedding.',
            whenToUse: 'Khi tài liệu BA complete cần đưa vào Knowledge Graph.',
            input: 'file_path, entities, relationships.',
          },
          {
            id: 'trace_impact',
            purpose: 'Phân tích tác động: dò node ảnh hưởng đến thành phần nào khác.',
            whenToUse: 'Khi đánh giá phạm vi CR / thay đổi.',
            input: 'node_id_field, node_id_value, depth (mặc định 2).',
          },
          {
            id: 'semantic_search_ba_nodes',
            purpose: 'Tìm thực thể BA bằng ngôn ngữ tự nhiên (vector search Neo4j).',
            whenToUse: 'Khi cần tìm node liên quan theo câu hỏi nghiệp vụ.',
            input: 'query, top_k.',
          },
          {
            id: 'batch_semantic_search_ba_nodes',
            purpose: 'Tìm kiếm semantic theo batch nhiều câu query.',
            whenToUse: 'Khi rà nhiều khái niệm / CR cùng lúc.',
            input: 'queries[], top_k.',
          },
          {
            id: 'query_ba_graph',
            purpose: 'Thực thi Cypher tùy ý (bắt buộc tham số hóa) trên đồ thị BA.',
            whenToUse: 'Khi cần truy vấn đồ thị linh hoạt.',
            input: 'query (Cypher), parameters.',
          },
        ],
      },
    ],
  },
];

export function isAi4baCourseCode(code?: string | null): boolean {
  return (code || '').toUpperCase() === 'AI4BA';
}
